import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { GET_FUB_SETTINGS } from "../../graphql/fubSettingQueries";
import { GET_COMPANY_DETAILS } from "../../graphql/companyQueries";
import { formatDate } from "../../utils/dateFormat";
import { CRM_LIST } from "./crmList";

const CrmIntegrationsListing = () => {
  const navigate = useNavigate();
  const { companyId: routeCompanyId } = useParams();
  let user = null;
  try {
    user = JSON.parse(localStorage.getItem("userInfo"));
  } catch {
    user = null;
  }
  const isAdminContext = !routeCompanyId;
  const targetCompanyId = routeCompanyId || user?.company?._id;

  const { data: fubData } = useQuery(GET_FUB_SETTINGS, {
    variables: { companyId: targetCompanyId },
    skip: !targetCompanyId,
    fetchPolicy: "network-only",
  });
  const fubConnected = !!fubData?.getFubSettings?.isConnected;

  // For admin context (no companyId in URL), layout doesn't render Header.
  // Fetch the super admin's own company to mirror the welcome strip from the mockup.
  const { data: adminCompanyData } = useQuery(GET_COMPANY_DETAILS, {
    variables: { companyId: user?.company?._id },
    skip: !isAdminContext || !user?.company?._id,
  });
  const adminCompany = adminCompanyData?.getCompanyById;

  const totalCount = CRM_LIST.length;
  const availableCount = CRM_LIST.filter(
    (c) => c.status === "available" && !(c.id === "fub" && fubConnected)
  ).length;
  const connectedCount = fubConnected ? 1 : 0;
  const comingSoonCount = CRM_LIST.filter((c) => c.status === "coming").length;

  const handleConnectClick = (crmId) => {
    if (crmId !== "fub") return;
    if (!targetCompanyId) return;
    navigate(`/company/${targetCompanyId}/integrations/fub/settings`);
  };

  return (
    <div className="w-full pb-12">
      {isAdminContext && (
        <div className="flex w-full text-secondary items-start justify-between pt-2 pb-6 border-b border-primary/30 mb-6">
          <button
            type="button"
            onClick={() => navigate("/administrator/dashboard")}
            className="inline-flex bg-primary px-4 py-2 text-white rounded font-semibold hover:opacity-90"
          >
            {"<<< Super Admin Dashboard"}
          </button>
          <div className="text-right">
            <div className="text-2xl text-secondary">
              Welcome back, {adminCompany?.companyName || ""}!
            </div>
            {adminCompany?.createdAt && (
              <div className="text-xs mt-1">
                FyrstUp user since: {formatDate(adminCompany.createdAt)}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="inline-flex items-center gap-2 bg-white shadow-sm rounded-full px-4 py-1.5 text-sm text-secondary mb-4">
        <PlugIcon />
        <span>Settings · Integrations</span>
      </div>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 m-0">
            CRM Integrations
          </h1>
          <p className="text-secondary text-sm mt-2 max-w-2xl">
            Connect your CRM platform to automatically sync buyers and property
            search criteria.
          </p>
        </div>
        <div className="flex gap-3">
          <StatPill value={availableCount} label="AVAILABLE" tone="primary" />
          <StatPill value={connectedCount} label="CONNECTED" tone="muted" />
          <StatPill value={comingSoonCount} label="COMING SOON" tone="muted" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {CRM_LIST.map((crm) => {
          const isFub = crm.id === "fub";
          const isConnected = isFub && fubConnected;
          const isAvailable = crm.status === "available";
          return (
            <div
              key={crm.id}
              className={`relative bg-white rounded-2xl shadow-sm p-6 flex flex-col ${
                isAvailable ? "ring-2 ring-primary" : ""
              }`}
            >
              {isAvailable && !isConnected && (
                <span className="absolute -top-3 left-4 bg-primary text-white text-[10px] font-bold px-3 py-1 rounded uppercase tracking-wider">
                  Ready to Connect
                </span>
              )}

              <div className="flex items-start justify-between mb-4">
                <div
                  className={`${crm.iconBg} w-12 h-12 rounded-xl text-white font-bold flex items-center justify-center text-base`}
                >
                  {crm.short}
                </div>
                {isAvailable ? (
                  isConnected ? (
                    <span className="bg-green/10 text-green text-xs font-medium px-3 py-1 rounded-full inline-flex items-center gap-1">
                      <DotIcon /> Connected
                    </span>
                  ) : (
                    <span className="bg-primary/10 text-primary_dark text-xs font-medium px-3 py-1 rounded-full inline-flex items-center gap-1">
                      <SparkleIcon /> Available
                    </span>
                  )
                ) : (
                  <span className="bg-gray-100 text-gray-500 text-xs font-medium px-3 py-1 rounded-full inline-flex items-center gap-1">
                    <ClockIcon /> Coming Soon
                  </span>
                )}
              </div>

              <h3 className="text-lg font-semibold text-gray-900 m-0">
                {crm.name}
              </h3>
              <p className="text-sm text-secondary mt-2 leading-relaxed">
                {crm.description}
              </p>

              <div className="border-t border-gray-100 mt-6 pt-4 flex items-center justify-between text-xs uppercase tracking-wider text-gray-400">
                <span className="font-semibold">Integration</span>
                {isAvailable ? (
                  <button
                    type="button"
                    onClick={() => handleConnectClick(crm.id)}
                    className="inline-flex items-center gap-2 bg-primary hover:bg-primary_dark text-white font-semibold px-4 py-2 rounded-lg normal-case tracking-normal text-sm"
                  >
                    {isConnected ? "Configure" : "Connect"} <ArrowRightIcon />
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-2 bg-gray-100 text-gray-400 font-medium px-4 py-2 rounded-lg normal-case tracking-normal text-sm cursor-not-allowed">
                    <ClockIcon /> Coming Soon
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const StatPill = ({ value, label, tone }) => (
  <div className="bg-white rounded-2xl shadow-sm px-5 py-2 text-center min-w-[88px]">
    <div
      className={`text-2xl font-bold leading-tight ${
        tone === "primary" ? "text-primary_dark" : "text-gray-700"
      }`}
    >
      {value}
    </div>
    <div className="text-[10px] font-semibold text-gray-400 tracking-wider">
      {label}
    </div>
  </div>
);

const PlugIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 2v6" />
    <path d="M15 2v6" />
    <path d="M6 8h12v4a6 6 0 0 1-12 0V8z" />
    <path d="M12 18v4" />
  </svg>
);

const SparkleIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2l1.6 5.4L19 9l-5.4 1.6L12 16l-1.6-5.4L5 9l5.4-1.6L12 2z" />
  </svg>
);

const ClockIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const DotIcon = () => (
  <span className="inline-block w-1.5 h-1.5 rounded-full bg-green" />
);

const ArrowRightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

export default CrmIntegrationsListing;
